import { useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { fetchUserDataBatch } from "@/lib/user-data-helpers";
import { auth } from "@lyder-talk/core/lib/auth-client";
import { db } from "@lyder-talk/core/lib/db-client";
import { apiGet, apiPost } from "@/lib/api-client";

export interface BookmarkStatus {
  bookmark_count: number;
  user_has_bookmarked: boolean;
}

export interface BookmarkedSession {
  bookmark_id: string;
  bookmarked_at: string;
  session_id: string;
  topic: string;
  slug: string;
  category_name: string | null;
  banner_url: string | null;
  is_public: boolean;
  created_at: string;
  user_id?: string;
  creator_email?: string;
  creator_avatar_url?: string | null;
  creator_display_name?: string;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  bookmark_count?: number;
  questions?: any[];
  vocabulary?: any[];
}

export const useSessionBookmark = (sessionId: string | null) => {
  const queryClient = useQueryClient();

  // Fetch bookmark status for a session
  const statusQuery = useQuery({
    queryKey: queryKeys.sessionBookmark.bySession(sessionId),
    queryFn: async (): Promise<BookmarkStatus> => {
      if (!sessionId) {
        return {
          bookmark_count: 0,
          user_has_bookmarked: false,
        };
      }

      // Use the helper function
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = await apiGet<Record<string, unknown>>(`/v1/sessions/${encodeURIComponent(sessionId)}/bookmark`);

      return {
        bookmark_count: Number(status?.bookmark_count ?? 0),
        user_has_bookmarked: Boolean(status?.user_has_bookmarked),
      };
    },
    enabled: !!sessionId,
    staleTime: 1000 * 60, // Cache for 1 minute
    refetchOnWindowFocus: false,
  });

  // Toggle bookmark mutation
  const toggleBookmarkMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const {
        data: { session },
      } = await auth.getSession();
      if (!session?.user) {
        throw new Error("You must be logged in to bookmark sessions");
      }

      // Check if user already bookmarked
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: existing } = await (db.from("session_bookmarks") as any)
        .select("id")
        .eq("practice_session_id", sessionId)
        .eq("user_id", session.user.id)
        .single();

      if (existing) {
        // Remove bookmark
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (db.from("session_bookmarks") as any)
          .delete()
          .eq("id", existing.id);
        if (error) throw error;
        return { action: "removed" as const };
      } else {
        // Add bookmark
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (db.from("session_bookmarks") as any).insert({
          practice_session_id: sessionId,
          user_id: session.user.id,
        });
        if (error) throw error;
        return { action: "added" as const };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessionBookmark.bySession(sessionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.bookmarkedSessions.all });
    },
  });

  const toggleBookmark = useCallback(
    async () => {
      if (!sessionId) return { action: "removed" as const };
      try {
        return await toggleBookmarkMutation.mutateAsync(sessionId);
      } catch (err) {
        console.error("Error toggling bookmark:", err);
        throw err;
      }
    },
    [sessionId, toggleBookmarkMutation]
  );

  return {
    status: statusQuery.data || {
      bookmark_count: 0,
      user_has_bookmarked: false,
    },
    loading: statusQuery.isLoading,
    isToggling: toggleBookmarkMutation.isPending,
    error: statusQuery.error,
    toggleBookmark,
    refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.sessionBookmark.bySession(sessionId) }),
  };
};

// Hook to fetch all bookmarked sessions for the current user
export const useBookmarkedSessions = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.bookmarkedSessions.all,
    queryFn: async (): Promise<BookmarkedSession[]> => {
      const {
        data: { session },
      } = await auth.getSession();

      if (!session?.user) {
        return [];
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bookmarkedSessions = await apiGet<BookmarkedSession[]>("/v1/sessions/bookmarked");

      // Fetch user_id for each session and then get user data
      const sessionIds = bookmarkedSessions.map((s) => s.session_id);
      if (sessionIds.length === 0) {
        return bookmarkedSessions;
      }

      // Get user_id, banner_url, and question/vocabulary COUNTS for each session.
      // `questions (id)` / `vocabulary (id)` embed the child keys only — list
      // cards need the counts, not the full content.
      const { data: sessionsData, error: sessionsError } = await db.from("practice_sessions")
        .select(`
          id,
          user_id,
          banner_url,
          questions (id),
          vocabulary (id)
        `)
        .in("id", sessionIds);

      if (sessionsError) {
        console.error("Error fetching session user IDs:", sessionsError);
        return bookmarkedSessions;
      }

      // Create a map of session_id to user_id, banner_url, and count placeholders.
      const sessionDetailMap = new Map<string, { userId: string; bannerUrl: string | null; questions: any[]; vocabulary: any[] }>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((sessionsData as any[]) || []).forEach((s: any) => {
        sessionDetailMap.set(s.id, {
          userId: s.user_id,
          bannerUrl: s.banner_url || null,
          questions: Array.from({ length: s.questions?.length ?? 0 }),
          vocabulary: Array.from({ length: s.vocabulary?.length ?? 0 }),
        });
      });

      // Get unique user IDs
      const userIds = [...new Set(Array.from(sessionDetailMap.values()).map(v => v.userId))];
      const userDataMap = await fetchUserDataBatch(userIds);

      // Add user_id, creator_email, creator_avatar_url, creator_display_name, banner_url, questions, vocabulary to each bookmarked session
      const sessionsWithCreator = bookmarkedSessions.map((session) => {
        const sessionData = sessionDetailMap.get(session.session_id);
        const userId = sessionData?.userId;
        const userData = userId ? userDataMap.get(userId) : null;
        return {
          ...session,
          user_id: userId,
          banner_url: sessionData?.bannerUrl || null,
          questions: sessionData?.questions || [],
          vocabulary: sessionData?.vocabulary || [],
          creator_email: userData?.email || "Unknown",
          creator_avatar_url: userData?.avatar_url || null,
          creator_display_name: userData?.display_name || "Unknown",
        };
      });

      return sessionsWithCreator;
    },
    enabled,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  return {
    bookmarkedSessions: query.data || [],
    loading: query.isLoading,
    error: query.error,
    refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.bookmarkedSessions.all }),
  };
};

